import React, { useState } from 'react';
import {
  Modal,
  TextInput,
  Switch,
  Button,
  Group,
  Stack,
  Text,
  Title,
  LoadingOverlay
} from '@mantine/core';
import { AssessmentSchedule } from 'services/lia/assessmentScheduleService';
import DateTimeField from 'components/DateTimeField';

interface CopyScheduleModalProps {
  opened: boolean;
  onClose: () => void;
  schedule: AssessmentSchedule | null;
  onSubmit: (_copyData: any) => void;
  isLoading?: boolean;
}

const CopyScheduleModal: React.FC<CopyScheduleModalProps> = ({
  opened,
  onClose,
  schedule,
  onSubmit,
  isLoading = false
}) => {
  const [scheduleName, setScheduleName] = useState('');
  const [scheduleStartDate, setScheduleStartDate] = useState<Date | null>(null);
  const [scheduleEndDate, setScheduleEndDate] = useState<Date | null>(null);
  const [copyParticipants, setCopyParticipants] = useState(false);
  const [copyRespondents, setCopyRespondents] = useState(false);
  const [copyQuestions, setCopyQuestions] = useState(true);
  const [copyModules, setCopyModules] = useState(true);

  // Reset form when modal opens with a new schedule
  React.useEffect(() => {
    if (opened && schedule) {
      setScheduleName(`Copy of ${schedule.name}`);

      // Convert UTC dates to local time
      // The API returns dates in UTC, so we create Date objects which will be in local time
      const startDate = new Date(schedule.startTime);
      const endDate = new Date(schedule.endTime);

      // Add 7 days to the dates (this maintains local time)
      startDate.setDate(startDate.getDate() + 7);
      endDate.setDate(endDate.getDate() + 7);

      setScheduleStartDate(startDate);
      setScheduleEndDate(endDate);

      // Default settings
      setCopyParticipants(false);
      setCopyRespondents(false);
      setCopyQuestions(true);
      setCopyModules(true);
    }
  }, [opened, schedule]);

  const handleSubmit = () => {
    if (!scheduleName || !scheduleStartDate || !scheduleEndDate) return;

    // Convert local dates to UTC while preserving the selected date/time values
    // This ensures the time displayed in the picker is the same time sent to the API
    const startDateUTC = new Date(
      Date.UTC(
        scheduleStartDate.getFullYear(),
        scheduleStartDate.getMonth(),
        scheduleStartDate.getDate(),
        scheduleStartDate.getHours(),
        scheduleStartDate.getMinutes(),
        scheduleStartDate.getSeconds()
      )
    );

    const endDateUTC = new Date(
      Date.UTC(
        scheduleEndDate.getFullYear(),
        scheduleEndDate.getMonth(),
        scheduleEndDate.getDate(),
        scheduleEndDate.getHours(),
        scheduleEndDate.getMinutes(),
        scheduleEndDate.getSeconds()
      )
    );

    const copyData = {
      scheduleName,
      scheduleStartDate: startDateUTC.toISOString(),
      scheduleEndDate: endDateUTC.toISOString(),
      copyParticipants,
      copyRespondents,
      copyQuestions,
      copyModules
    };

    onSubmit(copyData);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size='lg'
      centered
      title={<Title order={3}>Copy Assessment Schedule</Title>}
    >
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />

        <Stack gap='md' p={16}>
          {schedule && (
            <>
              <Text fw={500}>
                Original Schedule:{' '}
                <Text span fw={700}>
                  {schedule.name}
                </Text>
              </Text>

              <TextInput
                label='New Schedule Name'
                placeholder='Enter schedule name'
                value={scheduleName}
                onChange={(e) => setScheduleName(e.target.value)}
                required
              />

              <DateTimeField
                label='Start Date & Time'
                placeholder='Select start date and time'
                value={scheduleStartDate}
                onChange={setScheduleStartDate}
                required
              />

              <DateTimeField
                label='End Date & Time'
                placeholder='Select end date and time'
                value={scheduleEndDate}
                onChange={setScheduleEndDate}
                required
                minDate={scheduleStartDate || undefined}
              />

              <Title order={5} mt='md'>
                Copy Options
              </Title>

              <Switch
                label='Copy Questions'
                checked={copyQuestions}
                onChange={(e) => setCopyQuestions(e.currentTarget.checked)}
              />

              <Switch
                label='Copy Modules'
                checked={copyModules}
                onChange={(e) => setCopyModules(e.currentTarget.checked)}
              />

              <Group justify='flex-end' mt='xl'>
                <Button variant='outline' color='#7B2183' onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  color='#7B2183'
                  onClick={handleSubmit}
                  disabled={!scheduleName || !scheduleStartDate || !scheduleEndDate || isLoading}
                >
                  Copy Schedule
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </div>
    </Modal>
  );
};

export default CopyScheduleModal;
